import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { GlobalService } from '../../shared-services/global.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-mat-slider',
  standalone: true,
  imports: [CommonModule, MatSliderModule, FormsModule, MatInputModule,],
  templateUrl: './mat-slider.component.html',
  styleUrl: './mat-slider.component.scss',
})
export class MatSliderComponent implements OnInit, OnDestroy {
  @Input() data!: any; // input like bar-chart
  @Output() valueChange = new EventEmitter<{ start: number; end: number }>();

  min!: number;
  max!: number;
  step!: number;
  label!: string;

  rangeStart!: number;
  rangeEnd!: number;
  errorMsg: string = '';
  initialRangeStart!: number;
  initialRangeEnd!: number;
  private resetSub?: Subscription;
  private pendingEmitTimer?: ReturnType<typeof setTimeout>;
  private readonly emitDebounceMs = 200;

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private filterState: GlobalService,
  ) {}

  ngOnInit() {
    if (!this.data) {
      console.error('Slider data not provided');
      return;
    }

    // Seed with widget-level values so UI is stable even before/without API response.
    const widgetMin = Number(this.data?.min);
    const widgetMax = Number(this.data?.max);
    const widgetStep = Number(this.data?.step);
    const widgetStart = Number(this.data?.rangeStart);
    const widgetEnd = Number(this.data?.rangeEnd);
    this.min = Number.isFinite(widgetMin) ? widgetMin : 0;
    this.max = Number.isFinite(widgetMax) ? widgetMax : 100;
    this.step = Number.isFinite(widgetStep) && widgetStep > 0 ? widgetStep : 1;
    this.label = this.data?.name ?? this.data?.title ?? 'Slider';
    this.rangeStart = Number.isFinite(widgetStart) ? widgetStart : this.min;
    this.rangeEnd = Number.isFinite(widgetEnd) ? widgetEnd : this.max;
    this.initialRangeStart = this.rangeStart;
    this.initialRangeEnd = this.rangeEnd;

    this.configService.getConfig(this.data?.apiUrl, null, this.data?.method).subscribe({
      next: (res) => {
        const responseMin = Number(res?.data?.min);
        const responseMax = Number(res?.data?.max);
        const responseStep = Number(res?.data?.step);
        this.min = Number.isFinite(responseMin) ? responseMin : this.min;
        this.max = Number.isFinite(responseMax) ? responseMax : this.max;
        this.step = Number.isFinite(responseStep) && responseStep > 0 ? responseStep : this.step;
        this.label = res?.data?.name ?? this.label;

        // Take rangeStart from config and fix it
        const responseStart = Number(res?.data?.rangeStart);
        const responseEnd = Number(res?.data?.rangeEnd);
        this.rangeStart = Number.isFinite(responseStart) ? responseStart : this.rangeStart;
        this.rangeEnd = Number.isFinite(responseEnd) ? responseEnd : this.rangeEnd;
        const normalized = this.normalizeRange(this.rangeStart, this.rangeEnd);
        if (normalized) {
          this.rangeStart = normalized.start;
          this.rangeEnd = normalized.end;
          this.initialRangeStart = normalized.start;
          this.initialRangeEnd = normalized.end;
        }
      },
      error: (err) => {
        console.error(err);
      },
    });

    this.resetSub = this.filterState.reset$.subscribe(() => {
      this.reset();
    });
  }

  reset(): void {
    this.rangeStart = this.initialRangeStart;
    this.rangeEnd = this.initialRangeEnd;
    this.errorMsg = '';
    this.onRangeChange();
  }

  onRangeStartInput(raw: string): void {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      this.rangeStart = parsed;
    }
  }

  onRangeEndInput(raw: string): void {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      this.rangeEnd = parsed;
    }
  }

  changeRangeStart(delta: number): void {
    this.rangeStart = this.clampValue(Number(this.rangeStart) + Number(delta));
    this.validateAndEmit();
  }

  changeRangeEnd(delta: number): void {
    this.rangeEnd = this.clampValue(Number(this.rangeEnd) + Number(delta));
    this.validateAndEmit();
  }

  validateAndEmit() {
    const normalized = this.normalizeRange(this.rangeStart, this.rangeEnd);
    if (!normalized) {
      this.errorMsg = `Please enter a valid range between ${this.min} and ${this.max}`;
      return;
    }

    this.rangeStart = normalized.start;
    this.rangeEnd = normalized.end;
    this.errorMsg = '';
    this.onRangeChange();
  }

  onRangeChange() {
    const normalized = this.normalizeRange(this.rangeStart, this.rangeEnd);
    if (!normalized) {
      return;
    }

    this.rangeStart = normalized.start;
    this.rangeEnd = normalized.end;
    this.errorMsg = '';
    this.queueFilterEmit(normalized.start, normalized.end);
  }

  private queueFilterEmit(start: number, end: number): void {
    if (this.pendingEmitTimer) {
      clearTimeout(this.pendingEmitTimer);
    }

    this.pendingEmitTimer = setTimeout(() => {
      this.valueChange.emit({ start, end });
      if (this.data?.key) {
        this.globalFilterService.updateFilterSelection(
          this.data.key,
          [start, end],
          this.data.isQueryParam
        );
      }
    }, this.emitDebounceMs);
  }

  private clampValue(value: number): number {
    if (!Number.isFinite(value)) {
      return this.min;
    }
    return Math.min(this.max, Math.max(this.min, value));
  }

  private roundToTwo(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private normalizeRange(
    startValue: number,
    endValue: number,
  ): { start: number; end: number } | null {
    const start = Number(startValue);
    const end = Number(endValue);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return null;
    }

    const clampedStart = this.roundToTwo(this.clampValue(start));
    const clampedEnd = this.roundToTwo(this.clampValue(end));
    return {
      start: this.roundToTwo(Math.min(clampedStart, clampedEnd)),
      end: this.roundToTwo(Math.max(clampedStart, clampedEnd)),
    };
  }

  ngOnDestroy(): void {
    if (this.pendingEmitTimer) {
      clearTimeout(this.pendingEmitTimer);
    }
    this.resetSub?.unsubscribe();
  }
}
